<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import CannedResponse from './CannedResponse.vue';
import CannedResponsesPanel from './CannedResponsesPanel.vue';
import ReplyToMessage from './ReplyToMessage.vue';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
  extractTextFromMarkdown,
} from 'dashboard/helper/editorHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import aiReplySuggestionsAPI from 'dashboard/api/aiReplySuggestions';
import aiSuggestionsAPI from 'dashboard/api/aiSuggestions';
import aiReplyPreferenceAPI from 'dashboard/api/aiReplyPreference';
import { frontendURL } from 'dashboard/helper/URLHelper';
import SuggestionCard from 'dashboard/components/ai-reply/SuggestionCard.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    EmojiInput,
    ReplyBoxBanner,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    ResizableTextArea,
    CannedResponse,
    CannedResponsesPanel,
    SuggestionCard,
    TeleportWithDirection,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  props: {
    popOutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:popOutReplyBox'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showCannedResponsesPanel: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      translationEnabled: false,
      translationOriginalMessage: '',
      translationMeta: null,
      translationTargetLanguageMode: 'auto',
      translationAutoTargetLanguage: null,
      isTranslating: false,
      aiReplySuggestion: '',
      aiReplyStatus: 'idle',
      aiReplyPollTimeoutId: null,
      lastAiReplyMessageId: null,
      aiReplyFetchSource: 'unknown',
      agentAiReplyPreference: null,
      isGeneratingManually: false,
      agentAutoGenerateSuggestion: false,
      showAiPanel: false,
      isMobile: window.innerWidth < 640,
      emojiButtonElement: null,
      emojiPickerPosition: { top: 0, left: 0 },
      emojiPickerArrowTop: 0,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel || this.isAPIInbox;
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    isReplyRestricted() {
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    showTranslateToggle() {
      return !this.isOnPrivateNote;
    },
    translateDisabled() {
      return (
        !this.translationEnabled &&
        (this.isMessageEmpty || this.isRecordingAudio)
      );
    },
    detectedTranslationTargetLanguageCode() {
      const code =
        this.translationAutoTargetLanguage ||
        this.translationMeta?.target_language;
      return (code || '--').toString().toUpperCase();
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isATelegramUserInbox ||
        this.isALineChannel ||
        this.isAnInstagramChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? '(⌘ + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    emojiPickerStyle() {
      return {
        top: `${this.emojiPickerPosition.top}px`,
        left: `${this.emojiPickerPosition.left}px`,
        '--emoji-picker-arrow-top': `${this.emojiPickerArrowTop}px`,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      if (this.isATelegramUserInbox) {
        return false;
      }
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    isQuotedEmailReplyEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.QUOTED_EMAIL_REPLY
      );
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel || !this.isQuotedEmailReplyEnabled) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return (
        this.isAnEmailChannel &&
        !this.isOnPrivateNote &&
        this.isQuotedEmailReplyEnabled
      );
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    shouldShowAiReplySuggestion() {
      return (
        !this.isPrivate &&
        this.replyType === REPLY_EDITOR_MODES.REPLY &&
        ['loading', 'ready'].includes(this.aiReplyStatus) &&
        this.agentAiReplyPreference === 'suggestion'
      );
    },
    shouldShowGenerateButton() {
      return (
        !this.isPrivate &&
        this.replyType === REPLY_EDITOR_MODES.REPLY &&
        this.agentAiReplyPreference === 'suggestion' &&
        this.aiReplyStatus === 'idle'
      );
    },
    isAiReplyLoading() {
      return this.aiReplyStatus === 'loading';
    },
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      return false;
    },
    // ensure that the signature is plain text depending on `showRichContentEditor`
    signatureToApply() {
      return this.showRichContentEditor
        ? this.messageSignature
        : extractTextFromMarkdown(this.messageSignature);
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      console.info('[AI Reply] current chat watcher', {
        conversationId: conversation?.id,
        oldConversationId: oldConversation?.id,
      });
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        console.info('[AI Reply] reset last message id (chat switch)', {
          conversationId: conversation?.id,
        });
        this.lastAiReplyMessageId = null;
      }

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.tryAutoGenerateSuggestion(
        'conversationOpened',
        conversation?.messages
      );
      this.fetchAndSetReplyTo();
    },
    'currentChat.messages': {
      handler(newMessages, oldMessages) {
        this.onMessagesUpdated(newMessages, oldMessages);
      },
      deep: true,
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
        this.resetAiReplySuggestion(true);
      }
    },
    message(updatedMessage) {
      // Check if the message starts with a slash.
      const bodyWithoutSignature = removeSignature(
        updatedMessage,
        this.signatureToApply
      );
      const startsWithSlash = bodyWithoutSignature.startsWith('/');

      // Determine if the user is potentially typing a slash command.
      // This is true if the message starts with a slash and the rich content editor is not active.
      this.hasSlashCommand = startsWithSlash && !this.showRichContentEditor;
      this.showMentions = this.hasSlashCommand;

      // If a slash command is active, extract the command text after the slash.
      // If not, reset the mentionSearchKey.
      this.mentionSearchKey = this.hasSlashCommand
        ? bodyWithoutSignature.substring(1)
        : '';

      // Autosave the current message draft.
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    this.fetchAgentAiReplyPreference();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    window.addEventListener('resize', this.onWindowResize);
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    this.resetAiReplySuggestion();
    window.removeEventListener('resize', this.onWindowResize);
    this.teardownEmojiPickerListeners();
  },
  methods: {
    onWindowResize() {
      this.isMobile = window.innerWidth < 640;
      this.updateEmojiPickerPosition();
    },
    setupEmojiPickerListeners() {
      document.addEventListener(
        'mousedown',
        this.onEmojiPickerDocumentMouseDown
      );
      window.addEventListener('scroll', this.updateEmojiPickerPosition, true);
    },
    teardownEmojiPickerListeners() {
      document.removeEventListener(
        'mousedown',
        this.onEmojiPickerDocumentMouseDown
      );
      window.removeEventListener(
        'scroll',
        this.updateEmojiPickerPosition,
        true
      );
    },
    updateEmojiPickerPosition() {
      if (!this.showEmojiPicker || !this.emojiButtonElement) {
        return;
      }

      const triggerRect = this.emojiButtonElement.getBoundingClientRect();
      const pickerWidth = 320;
      const pickerHeight = 300;
      const gap = 8;
      const arrowHeight = 12;
      const arrowHalfHeight = arrowHeight / 2;
      const defaultArrowTop = pickerHeight - 22;
      const viewportPadding = 8;
      const triggerCenterY = triggerRect.top + triggerRect.height / 2;

      const maxLeft = Math.max(
        viewportPadding,
        window.innerWidth - pickerWidth - viewportPadding
      );
      const left = Math.min(
        Math.max(viewportPadding, triggerRect.left - pickerWidth - gap),
        maxLeft
      );

      const maxTop = Math.max(
        viewportPadding,
        window.innerHeight - pickerHeight - viewportPadding
      );
      const top = Math.min(
        Math.max(
          viewportPadding,
          triggerCenterY - defaultArrowTop - arrowHalfHeight
        ),
        maxTop
      );
      const arrowTop = Math.min(
        Math.max(viewportPadding, triggerCenterY - top - arrowHalfHeight),
        pickerHeight - viewportPadding - arrowHeight
      );

      this.emojiPickerPosition = {
        left,
        top,
      };
      this.emojiPickerArrowTop = arrowTop;
    },
    onEmojiPickerDocumentMouseDown(event) {
      const pickerElement = this.$refs.emojiPickerWrapper;
      const target = event.target;

      if (
        pickerElement?.contains(target) ||
        this.emojiButtonElement?.contains(target)
      ) {
        return;
      }

      this.hideEmojiPicker();
    },
    onFileUploadForTelegramUserInbox(file) {
      if (!this.isATelegramUserInbox) {
        this.onFileUpload(file);
        return;
      }

      const mime = file?.file?.type || file?.type || '';
      if (!mime.startsWith('image/')) {
        useAlert(
          this.$t('CONVERSATION.REPLYBOX.TELEGRAM_USER_ONLY_IMAGE_SUPPORTED')
        );
        return;
      }

      const existingImages = this.attachedFiles.filter(attachment => {
        const t =
          attachment?.resource?.file?.type || attachment?.resource?.type || '';
        return t.startsWith('image/');
      });

      if (existingImages.length >= 1) {
        useAlert(
          this.$t('CONVERSATION.REPLYBOX.TELEGRAM_USER_SINGLE_IMAGE_ONLY')
        );
        return;
      }

      this.onFileUpload(file);
    },
    async fetchAgentAiReplyPreference() {
      try {
        const response = await aiReplyPreferenceAPI.get();
        this.agentAiReplyPreference = response.data.mode;
        this.agentAutoGenerateSuggestion =
          response.data.auto_generate_suggestion || false;
        this.tryAutoGenerateSuggestion('conversationOpened');
      } catch (error) {
        // 如果获取失败，默认为 suggestion 模式（向后兼容）
        this.agentAiReplyPreference = 'suggestion';
        this.agentAutoGenerateSuggestion = false;
      }
    },
    resetAiReplySuggestion(force = false) {
      console.info('[AI Reply] reset suggestion', {
        conversationId: this.conversationIdByRoute,
        lastAiReplyMessageId: this.lastAiReplyMessageId,
        force,
      });

      // Guard: Don't reset if actively loading from messagesUpdated
      // UNLESS force=true (e.g., validation failed)
      if (
        !force &&
        this.aiReplyStatus === 'loading' &&
        this.aiReplyFetchSource === 'messagesUpdated'
      ) {
        console.info('[AI Reply] reset blocked by guard', {
          conversationId: this.conversationIdByRoute,
          aiReplyStatus: this.aiReplyStatus,
          aiReplyFetchSource: this.aiReplyFetchSource,
        });
        return;
      }

      if (this.aiReplyPollTimeoutId) {
        clearTimeout(this.aiReplyPollTimeoutId);
        this.aiReplyPollTimeoutId = null;
      }
      this.aiReplyStatus = 'idle';
      this.aiReplySuggestion = '';
    },
    findLatestIncomingMessage(messages) {
      const candidates = (messages || [])
        .filter(message => message && message.created_at)
        .filter(
          message =>
            message.message_type === 0 &&
            message.sender_type === 'Contact' &&
            !message.private
        )
        .slice()
        .sort((a, b) => Number(a.created_at) - Number(b.created_at));

      return candidates.at(-1);
    },
    getReplyToMessageId(message) {
      return (
        message?.content_attributes?.in_reply_to ||
        message?.contentAttributes?.in_reply_to ||
        message?.contentAttributes?.inReplyTo ||
        message?.content_attributes?.inReplyTo ||
        null
      );
    },
    hasReplyAfterIncomingMessage(messages, incomingMessage) {
      if (!incomingMessage) return false;
      const normalizedMessages = (messages || [])
        .filter(message => message && message.created_at)
        .slice()
        .sort((a, b) => Number(a.created_at) - Number(b.created_at));

      return normalizedMessages.some(message => {
        const isAfterIncoming =
          Number(message.created_at) > Number(incomingMessage.created_at);
        if (!isAfterIncoming) return false;

        const isAgentReply =
          message.sender_type === 'User' ||
          (message.ai_generated === true && message.sender_type !== 'AgentBot');
        if (!isAgentReply) return false;

        const replyToId = this.getReplyToMessageId(message);
        if (replyToId) {
          return replyToId === incomingMessage.id;
        }

        return true;
      });
    },
    getLatestIncomingMessage(messages) {
      const lastIncomingMessage = this.currentChat?.last_incoming_message;
      if (lastIncomingMessage?.id) {
        return lastIncomingMessage;
      }

      return this.findLatestIncomingMessage(messages);
    },
    tryAutoGenerateSuggestion(
      source,
      messages = this.currentChat?.messages || []
    ) {
      const latestIncomingMessage = this.getLatestIncomingMessage(messages);
      if (!latestIncomingMessage) return;

      if (
        this.agentAiReplyPreference !== 'suggestion' ||
        this.agentAutoGenerateSuggestion !== true
      ) {
        console.info('[AI Reply] skip auto-generate (preference)', {
          conversationId: this.conversationIdByRoute,
          mode: this.agentAiReplyPreference,
          auto_generate_suggestion: this.agentAutoGenerateSuggestion,
          source,
        });
        return;
      }

      if (
        this.lastAiReplyMessageId === latestIncomingMessage.id &&
        (this.aiReplyStatus === 'loading' ||
          (this.aiReplyStatus === 'ready' && this.aiReplySuggestion))
      ) {
        return;
      }

      this.lastAiReplyMessageId = latestIncomingMessage.id;
      this.resetAiReplySuggestion();
      this.requestAiReplySuggestion(source);
    },
    onMessagesUpdated(newMessages, oldMessages) {
      console.info('[AI Reply] messages updated', {
        conversationId: this.conversationIdByRoute,
        newCount: newMessages?.length || 0,
        oldCount: oldMessages?.length || 0,
        lastIncomingId: this.currentChat?.last_incoming_message?.id || null,
        lastAiReplyMessageId: this.lastAiReplyMessageId,
      });
      const lastIncomingMessage = this.findLatestIncomingMessage(newMessages);
      const hasReplyAfterIncoming = this.hasReplyAfterIncomingMessage(
        newMessages,
        lastIncomingMessage
      );
      if (hasReplyAfterIncoming) {
        console.info('[AI Reply] messages skip (has reply after incoming)', {
          conversationId: this.conversationIdByRoute,
          lastIncomingMessageId: lastIncomingMessage?.id || null,
        });
        return;
      }
      const latestIncomingMessage = this.getLatestIncomingMessage(newMessages);
      if (!latestIncomingMessage) return;

      if (this.lastAiReplyMessageId === latestIncomingMessage.id) return;

      const previousIncomingMessage =
        this.findLatestIncomingMessage(oldMessages);
      if (previousIncomingMessage?.id === latestIncomingMessage.id) return;

      console.info('[AI Reply] trigger suggestion for incoming message', {
        conversationId: this.conversationIdByRoute,
        messageId: latestIncomingMessage.id,
      });
      this.tryAutoGenerateSuggestion('messagesUpdated', newMessages);
    },
    scheduleAiReplyPoll() {
      if (this.aiReplyPollTimeoutId) {
        clearTimeout(this.aiReplyPollTimeoutId);
      }
      console.info('[AI Reply] schedule poll', {
        conversationId: this.conversationIdByRoute,
      });
      this.aiReplyPollTimeoutId = setTimeout(() => {
        this.aiReplyFetchSource = 'poll';
        this.fetchAiReplySuggestion();
      }, 2000);
    },
    requestAiReplySuggestion(source) {
      this.aiReplyFetchSource = source;
      if (
        this.aiReplyStatus === 'idle' ||
        (this.aiReplyStatus === 'loading' &&
          source === 'conversationOpened' &&
          !this.aiReplySuggestion) ||
        (this.aiReplyStatus === 'loading' &&
          this.aiReplyFetchSource === 'messagesUpdated' &&
          !this.aiReplySuggestion)
      ) {
        this.aiReplyStatus = 'loading';
        this.aiReplySuggestion = '';
      }
      console.info('[AI Reply] request suggestion', {
        conversationId: this.conversationIdByRoute,
        source,
      });
      this.fetchAiReplySuggestion();
    },
    async fetchAiReplySuggestion() {
      console.info('[AI Reply] fetch start', {
        conversationId: this.conversationIdByRoute,
        isPrivate: this.isPrivate,
        replyType: this.replyType,
        aiReplyStatus: this.aiReplyStatus,
        lastAiReplyMessageId: this.lastAiReplyMessageId,
        source: this.aiReplyFetchSource,
      });
      if (this.aiReplyStatus === 'ready' && this.aiReplySuggestion) {
        console.info('[AI Reply] fetch skip (already ready)', {
          conversationId: this.conversationIdByRoute,
        });
        return;
      }
      if (
        !this.conversationIdByRoute ||
        this.isPrivate ||
        this.replyType !== REPLY_EDITOR_MODES.REPLY
      ) {
        console.info('[AI Reply] fetch skip (guard)', {
          conversationId: this.conversationIdByRoute,
        });
        return;
      }

      const messages = this.currentChat?.messages || [];
      if (messages.length > 0) {
        const normalizedMessages = messages
          .filter(message => message && message.created_at)
          .slice()
          .sort((a, b) => Number(a.created_at) - Number(b.created_at));

        const lastIncomingMessage = normalizedMessages
          .filter(
            message =>
              message.message_type === 0 &&
              message.sender_type === 'Contact' &&
              !message.private
          )
          .at(-1);
        const resolvedIncomingMessage =
          lastIncomingMessage || this.currentChat?.last_incoming_message;

        const hasReplyAfterIncoming = this.hasReplyAfterIncomingMessage(
          normalizedMessages,
          resolvedIncomingMessage
        );

        if (!resolvedIncomingMessage || hasReplyAfterIncoming) {
          console.info('[AI Reply] fetch skip (messages)', {
            conversationId: this.conversationIdByRoute,
            lastIncomingMessageId: resolvedIncomingMessage?.id || null,
            hasReplyAfterIncoming,
          });
          this.resetAiReplySuggestion(true);
          return;
        }
      } else {
        const lastIncomingMessage = this.currentChat?.last_incoming_message;
        if (!lastIncomingMessage) {
          console.info('[AI Reply] fetch skip (no last incoming)', {
            conversationId: this.conversationIdByRoute,
          });
          this.resetAiReplySuggestion(true);
          return;
        }

        const lastOutgoingMessage = this.currentChat?.last_outgoing_message;
        const hasReplyAfterIncoming = lastOutgoingMessage
          ? this.hasReplyAfterIncomingMessage(
              [lastOutgoingMessage],
              lastIncomingMessage
            )
          : false;

        if (hasReplyAfterIncoming) {
          console.info('[AI Reply] fetch skip (has reply after incoming)', {
            conversationId: this.conversationIdByRoute,
            lastIncomingMessageId: lastIncomingMessage?.id || null,
            lastOutgoingMessageId: lastOutgoingMessage?.id || null,
          });
          this.resetAiReplySuggestion(true);
          return;
        }
      }

      if (
        this.aiReplyStatus === 'idle' ||
        (this.aiReplyStatus === 'loading' &&
          this.aiReplyFetchSource === 'conversationOpened' &&
          !this.aiReplySuggestion) ||
        (this.aiReplyStatus === 'loading' &&
          this.aiReplyFetchSource === 'messagesUpdated' &&
          !this.aiReplySuggestion)
      ) {
        this.aiReplyStatus = 'loading';
        try {
          console.info('[AI Reply] generate suggestion', {
            conversationId: this.conversationIdByRoute,
          });
          const { data } = await aiSuggestionsAPI.generate(
            this.conversationIdByRoute
          );
          const suggestion = data?.suggestion || data?.content;

          if (suggestion) {
            this.aiReplyStatus = 'ready';
            this.aiReplySuggestion = suggestion;
            console.info('[AI Reply] suggestion ready (generate)', {
              conversationId: this.conversationIdByRoute,
            });
            return;
          }
        } catch (error) {
          this.aiReplyStatus = 'idle';
          console.info('[AI Reply] generate failed', {
            conversationId: this.conversationIdByRoute,
          });
          return;
        }
      }

      this.aiReplyStatus = 'loading';
      try {
        console.info('[AI Reply] poll suggestion', {
          conversationId: this.conversationIdByRoute,
        });
        const { data } = await aiReplySuggestionsAPI.get(
          this.conversationIdByRoute
        );

        if (data.status === 'ready') {
          this.aiReplyStatus = 'ready';
          this.aiReplySuggestion = data.content;
          console.info('[AI Reply] suggestion ready (poll)', {
            conversationId: this.conversationIdByRoute,
          });
          return;
        }

        this.aiReplyStatus = 'loading';
        this.aiReplySuggestion = '';
        this.scheduleAiReplyPoll();
      } catch (error) {
        this.aiReplyStatus = 'idle';
        console.info('[AI Reply] poll failed', {
          conversationId: this.conversationIdByRoute,
        });
      }
    },
    applyAiReplySuggestion() {
      if (!this.aiReplySuggestion) return;
      this.replaceText(this.aiReplySuggestion);
      this.resetAiReplySuggestion();
    },
    async sendAiReplySuggestion() {
      if (!this.aiReplySuggestion) return;

      this.replaceText(this.aiReplySuggestion);
      const didSend = await this.onSendReply();
      if (didSend) {
        this.resetAiReplySuggestion();
      }
    },
    dismissAiReplySuggestion() {
      // 允许用户强制关闭卡住的加载状态
      this.resetAiReplySuggestion(true);
    },
    async handleManualGenerate() {
      this.isGeneratingManually = true;
      try {
        await aiReplySuggestionsAPI.create(this.conversationIdByRoute);
        this.requestAiReplySuggestion('manual');
      } catch (error) {
        console.error('[AI Reply] Manual generation failed', error);
        this.$track(CONVERSATION_EVENTS.AI_REPLY_GENERATION_FAILED);
      } finally {
        this.isGeneratingManually = false;
      }
    },
    handleInsert(article) {
      const { url, title } = article;
      if (this.isRichEditorEnabled) {
        // Removing empty lines from the title
        const lines = title.split('\n');
        const nonEmptyLines = lines.filter(line => line.trim() !== '');
        const filteredMarkdown = nonEmptyLines.join(' ');
        emitter.emit(
          BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
          `[${filteredMarkdown}](${url})`
        );
      } else {
        this.addIntoEditor(
          `${this.$t('CONVERSATION.REPLYBOX.INSERT_READ_MORE')} ${url}`
        );
      }

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.isQuotedEmailReplyEnabled &&
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }
      if (this.showRichContentEditor) {
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        return this.sendWithSignature
          ? appendSignature(
              message,
              this.messageSignature,
              effectiveChannelType
            )
          : removeSignature(
              message,
              this.messageSignature,
              effectiveChannelType
            );
      }
      return this.sendWithSignature
        ? appendSignature(message, this.signatureToApply)
        : removeSignature(message, this.signatureToApply);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
            this.hideMentions();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: e => {
            if (this.isAValidEvent('cmd_enter')) {
              e.preventDefault();
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedResponsesPanel &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput?.$el?.blur();
      }

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUploadForTelegramUserInbox({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    openCannedResponsesPanel() {
      this.hideEmojiPicker();
      this.hideMentions();
      this.showUserMentions = false;
      this.showCannedMenu = false;
      this.showVariablesMenu = false;
      this.showCannedResponsesPanel = true;
    },
    closeCannedResponsesPanel() {
      this.showCannedResponsesPanel = false;
      this.$nextTick(() => {
        if (this.showRichContentEditor) {
          this.onFocus();
          return;
        }

        this.$refs.messageInput?.focus();
      });
    },
    handleCannedResponsePanelSelect(item) {
      if (!item?.content) return;

      this.insertCannedResponseAtCursor(item.content);
      this.closeCannedResponsesPanel();
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return false;
      }
      if (!this.showMentions) {
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // When users send messages containing both text and attachments on Instagram, Instagram treats them as separate messages.
        // Although Chatwoot combines these into a single message, Instagram sends separate echo events for each component.
        // This can create duplicate messages in Chatwoot. To prevent this issue, we'll handle text and attachments as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        if ((isOnWhatsApp || isOnInstagram) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(this.message);
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(messagePayload);
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popOutReplyBox', false);
        return true;
      }

      return false;
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(messagePayload);
      });
    },
    sendMessageAnalyticsData(isPrivate) {
      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          return this.confirmOnSendReply();
        }
        return false;
      } else {
        return this.confirmOnSendReply();
      }
    },
    async toggleTranslation() {
      if (this.translationEnabled) {
        this.disableTranslation();
        return;
      }

      if (this.translateDisabled) {
        return;
      }

      this.translationTargetLanguageMode = 'auto';
      this.translationAutoTargetLanguage = null;
      await this.previewTranslation();
    },
    async setTranslationTargetLanguageMode(nextMode) {
      if (this.isTranslating) {
        return;
      }

      if (nextMode === this.translationTargetLanguageMode) {
        return;
      }

      const previousMode = this.translationTargetLanguageMode;
      this.translationTargetLanguageMode = nextMode;
      const ok = await this.previewTranslation();

      if (!ok) {
        this.translationTargetLanguageMode = previousMode;
      }
    },
    async previewTranslation() {
      if (this.isTranslating) {
        return false;
      }

      const wasTranslationEnabled = this.translationEnabled;
      const originalMessage = this.translationOriginalMessage || this.message;
      const targetLanguage =
        this.translationTargetLanguageMode === 'auto'
          ? undefined
          : this.translationTargetLanguageMode;
      this.isTranslating = true;

      try {
        const response = await this.$store.dispatch('previewTranslation', {
          conversationId: this.currentChat.id,
          content: originalMessage,
          targetLanguage,
        });

        const translatedContent = response?.data?.content?.toString() || '';
        if (!translatedContent) {
          throw new Error('Translation failed');
        }

        if (!this.translationOriginalMessage) {
          this.translationOriginalMessage = originalMessage;
        }
        this.translationMeta = {
          target_language: response?.data?.target_language,
          detected_language: response?.data?.detected_language,
          glossary: response?.data?.glossary,
        };

        if (
          this.translationTargetLanguageMode === 'auto' &&
          response?.data?.target_language
        ) {
          this.translationAutoTargetLanguage = response.data.target_language;
        }

        this.translationEnabled = true;
        this.message = translatedContent;
        return true;
      } catch (_error) {
        if (!wasTranslationEnabled) {
          this.translationEnabled = false;
        }
        useAlert(this.$t('CONVERSATION.REPLYBOX.TRANSLATE_ERROR'));
        return false;
      } finally {
        this.isTranslating = false;
      }
    },
    disableTranslation() {
      if (this.translationOriginalMessage) {
        this.message = this.translationOriginalMessage;
      }
      this.resetTranslationState();
    },
    resetTranslationState() {
      this.translationEnabled = false;
      this.translationOriginalMessage = '';
      this.translationMeta = null;
      this.translationTargetLanguageMode = 'auto';
      this.translationAutoTargetLanguage = null;
      this.isTranslating = false;
    },
    openGlossarySettings() {
      window.open(
        frontendURL(`accounts/${this.accountId}/settings/glossary/list`),
        '_blank'
      );
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    replaceText(message) {
      if (this.sendWithSignature && !this.private) {
        // if signature is enabled, append it to the message
        // appendSignature ensures that the signature is not duplicated
        // so we don't need to check if the signature is already present
        if (this.showRichContentEditor) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          message = appendSignature(
            message,
            this.messageSignature,
            effectiveChannelType
          );
        } else {
          message = appendSignature(message, this.signatureToApply);
        }
      }

      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });

      setTimeout(() => {
        useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    insertCannedResponseAtCursor(message) {
      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });

      useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
      this.addIntoEditor(updatedMessage);
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox)
        this.replyType = mode;
      if (mode === REPLY_EDITOR_MODES.NOTE) {
        this.resetTranslationState();
      }
      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    insertIntoTextEditor(text, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        text +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    addIntoEditor(content) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = content;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const { selectionStart, selectionEnd } = this.$refs.messageInput.$el;
        this.insertIntoTextEditor(content, selectionStart, selectionEnd);
      }
    },
    clearMessage() {
      this.message = '';
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        if (this.showRichContentEditor) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          this.message = appendSignature(
            this.message,
            this.messageSignature,
            effectiveChannelType
          );
        } else {
          this.message = appendSignature(this.message, this.signatureToApply);
        }
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
      this.resetTranslationState();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker(event) {
      if (this.showEmojiPicker) {
        this.hideEmojiPicker();
        return;
      }

      this.emojiButtonElement =
        event?.currentTarget || event?.target?.closest?.('button') || null;
      this.showEmojiPicker = true;
      this.$nextTick(() => {
        this.updateEmojiPickerPosition();
        this.setupEmojiPickerListeners();
      });
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (!this.showEmojiPicker) {
        return;
      }

      this.showEmojiPicker = false;
      this.emojiButtonElement = null;
      this.teardownEmojiPickerListeners();
    },
    hideMentions() {
      this.showMentions = false;
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    applyTranslationAttributes(payload) {
      if (!this.translationEnabled || !this.translationOriginalMessage) {
        return payload;
      }

      const autoTranslate = {
        original_text: this.translationOriginalMessage,
        target_language: this.translationMeta?.target_language,
        detected_language: this.translationMeta?.detected_language,
        provider: 'volcengine',
      };

      return {
        ...payload,
        contentAttributes: {
          ...payload.contentAttributes,
          send_translation: true,
          auto_translate: autoTranslate,
        },
      };
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = this.isAnInstagramChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram, we need a separate text message
      // For WhatsApp, we only need a text message if there are no attachments
      if (
        (this.isAnInstagramChannel && this.message) ||
        (!this.isAnInstagramChannel && hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);
        messagePayload = this.applyTranslationAttributes(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);
      messagePayload = this.applyTranslationAttributes(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    togglePopout() {
      this.$emit('update:popOutReplyBox', !this.popOutReplyBox);
    },
    toggleAiPanel() {
      this.showAiPanel = !this.showAiPanel;
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <ReplyTopPanel
      :mode="replyType"
      :is-reply-restricted="isReplyRestricted"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :popout-reply-box="popOutReplyBox"
      @set-reply-mode="setReplyMode"
      @toggle-popout="togglePopout"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <div class="reply-box__top">
      <ReplyToMessage
        v-if="shouldShowReplyToMessage"
        :message="inReplyTo"
        @dismiss="resetReplyToMessage"
      />
      <div
        v-if="shouldShowGenerateButton"
        class="reply-box__ai-generate mb-3 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-700 dark:bg-slate-800"
      >
        <span class="text-xs text-slate-600 dark:text-slate-400">
          {{ $t('AI_REPLY.GENERATE_SUGGESTION_PROMPT') }}
        </span>
        <button
          class="rounded-md bg-woot-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-woot-600 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isGeneratingManually"
          @click="handleManualGenerate"
        >
          {{
            isGeneratingManually
              ? $t('AI_REPLY.GENERATING')
              : $t('AI_REPLY.GENERATE_SUGGESTION')
          }}
        </button>
      </div>
      <SuggestionCard
        v-if="shouldShowAiReplySuggestion"
        :content="aiReplySuggestion"
        :is-loading="isAiReplyLoading"
        class="reply-box__ai-suggestion mb-3"
        @edit="applyAiReplySuggestion"
        @send="sendAiReplySuggestion"
        @dismiss="dismissAiReplySuggestion"
      />
      <CannedResponsesPanel
        :show="showCannedResponsesPanel"
        @close="closeCannedResponsesPanel"
        @select="handleCannedResponsePanelSelect"
      />
      <CannedResponse
        v-if="showMentions && hasSlashCommand"
        v-on-clickaway="hideMentions"
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        @replace="replaceText"
      />
      <ReplyEmailHead
        v-if="showReplyHead"
        v-model:cc-emails="ccEmails"
        v-model:bcc-emails="bccEmails"
        v-model:to-emails="toEmails"
      />
      <AudioRecorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @recorder-progress-changed="onRecordProgressChanged"
        @finish-record="onFinishRecorder"
        @play="recordingAudioState = 'playing'"
        @pause="recordingAudioState = 'paused'"
      />
      <ResizableTextArea
        v-else-if="!showRichContentEditor"
        ref="messageInput"
        v-model="message"
        class="rounded-none input"
        :placeholder="messagePlaceHolder"
        :min-height="isMobile ? 2 : 2"
        :rows="isMobile ? 1 : 2"
        :signature="signatureToApply"
        allow-signature
        :send-with-signature="sendWithSignature"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
      />
      <WootMessageEditor
        v-else
        v-model="message"
        :editor-id="editorStateId"
        class="input popover-prosemirror-menu"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="2"
        enable-variables
        :variables="messageVariables"
        :signature="messageSignature"
        allow-signature
        :channel-type="channelType"
        :medium="inbox.medium"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
      />
      <div
        v-if="translationEnabled && translationOriginalMessage"
        class="mt-3 rounded-lg bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12"
      >
        <div class="flex items-center gap-2 text-xs text-n-slate-11">
          <span>
            {{ $t('CONVERSATION.REPLYBOX.TRANSLATE_PREVIEW.ORIGINAL_LABEL') }}
          </span>
          <span v-if="translationMeta?.target_language" class="uppercase">
            {{ translationMeta.target_language }}
          </span>
          <span class="text-n-slate-10">
            {{ $t('CONVERSATION.REPLYBOX.TRANSLATE_PREVIEW.PROVIDER') }}
          </span>
        </div>
        <div
          class="mt-2 flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
        >
          <span class="shrink-0">
            {{ $t('CONVERSATION.REPLYBOX.TRANSLATE_TARGET.LABEL') }}
          </span>
          <div
            class="inline-flex items-center gap-1 rounded-lg bg-n-alpha-black2 p-1"
            :class="{ 'opacity-60': isTranslating }"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs"
              :class="
                translationTargetLanguageMode === 'auto'
                  ? 'bg-n-alpha-3 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-3'
              "
              :aria-pressed="translationTargetLanguageMode === 'auto'"
              :disabled="isTranslating"
              @click="setTranslationTargetLanguageMode('auto')"
            >
              <span>
                {{
                  $t('CONVERSATION.REPLYBOX.TRANSLATE_TARGET.AUTO_OPTION', {
                    languageCode: detectedTranslationTargetLanguageCode,
                  })
                }}
              </span>
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs"
              :class="
                translationTargetLanguageMode === 'zh'
                  ? 'bg-n-alpha-3 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-3'
              "
              :aria-pressed="translationTargetLanguageMode === 'zh'"
              :disabled="isTranslating"
              @click="setTranslationTargetLanguageMode('zh')"
            >
              {{ $t('CONVERSATION.REPLYBOX.TRANSLATE_TARGET.CHINESE_OPTION') }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs"
              :class="
                translationTargetLanguageMode === 'en'
                  ? 'bg-n-alpha-3 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-3'
              "
              :aria-pressed="translationTargetLanguageMode === 'en'"
              :disabled="isTranslating"
              @click="setTranslationTargetLanguageMode('en')"
            >
              {{ $t('CONVERSATION.REPLYBOX.TRANSLATE_TARGET.ENGLISH_OPTION') }}
            </button>
          </div>
        </div>
        <div
          class="mt-2 flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
        >
          <span>
            {{
              translationMeta?.glossary?.matched
                ? $t('CONVERSATION.REPLYBOX.GLOSSARY.MATCHED_LABEL', {
                    name: translationMeta?.glossary?.name,
                  })
                : translationMeta?.glossary?.exists
                  ? $t('CONVERSATION.REPLYBOX.GLOSSARY.UNAVAILABLE_LABEL', {
                      name: translationMeta?.glossary?.name,
                    })
                  : $t('CONVERSATION.REPLYBOX.GLOSSARY.MISSING_LABEL', {
                      targetLanguage:
                        translationMeta?.glossary?.target_language ||
                        translationMeta?.target_language,
                    })
            }}
          </span>
          <button
            type="button"
            class="rounded-md border border-n-slate-6 bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-n-slate-6/40"
            @click="openGlossarySettings"
          >
            {{ $t('CONVERSATION.REPLYBOX.GLOSSARY.MANAGE') }}
          </button>
        </div>
        <p class="mt-1 whitespace-pre-wrap">
          {{ translationOriginalMessage }}
        </p>
      </div>
      <QuotedEmailPreview
        v-if="shouldShowQuotedPreview"
        :quoted-email-text="quotedEmailText"
        :preview-text="quotedEmailPreviewText"
        @toggle="toggleQuotedReply"
      />
    </div>
    <div
      v-if="hasAttachments && !showAudioRecorderEditor"
      class="attachment-preview-box"
      @paste="onPaste"
    >
      <AttachmentPreview
        class="flex-col mt-4"
        :attachments="attachedFiles"
        @remove-attachment="removeAttachment"
      />
    </div>
    <MessageSignatureMissingAlert
      v-if="isSignatureEnabledForInbox && !isSignatureAvailable"
    />
    <!-- 移动端 AI 建议浮动面板 -->
    <div
      v-if="
        showAiPanel && (shouldShowGenerateButton || shouldShowAiReplySuggestion)
      "
      class="reply-box__ai-panel mx-3 mb-1 rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800 shadow-md"
    >
      <div
        v-if="shouldShowGenerateButton"
        class="flex items-center justify-between px-3 py-2"
      >
        <span class="text-xs text-slate-600 dark:text-slate-400">
          {{ $t('AI_REPLY.GENERATE_SUGGESTION_PROMPT') }}
        </span>
        <button
          class="rounded-md bg-woot-500 px-2 py-1 text-xs font-medium text-white hover:bg-woot-600 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isGeneratingManually"
          @click="handleManualGenerate"
        >
          {{
            isGeneratingManually
              ? $t('AI_REPLY.GENERATING')
              : $t('AI_REPLY.GENERATE_SUGGESTION')
          }}
        </button>
      </div>
      <SuggestionCard
        v-if="shouldShowAiReplySuggestion"
        :content="aiReplySuggestion"
        :is-loading="isAiReplyLoading"
        @edit="applyAiReplySuggestion; showAiPanel = false"
        @send="sendAiReplySuggestion; showAiPanel = false"
        @dismiss="dismissAiReplySuggestion; showAiPanel = false"
      />
    </div>
    <ReplyBottomPanel
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :enable-whats-app-templates="showWhatsappTemplates"
      :enable-content-templates="showContentTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :is-note="isPrivate"
      :on-file-upload="onFileUploadForTelegramUserInbox"
      :on-send="onSendReply"
      :conversation-type="conversationType"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
      :quoted-reply-enabled="quotedReplyPreference"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      :show-translate-toggle="showTranslateToggle"
      :translate-active="translationEnabled"
      :translate-disabled="translateDisabled"
      :is-translating="isTranslating"
      :message="message"
      :portal-slug="connectedPortalSlug"
      :new-conversation-modal-active="newConversationModalActive"
      :show-ai-panel-button="
        shouldShowGenerateButton || shouldShowAiReplySuggestion
      "
      :ai-panel-active="showAiPanel"
      @toggle-canned-responses="openCannedResponsesPanel"
      @select-whatsapp-template="openWhatsappTemplateModal"
      @select-content-template="openContentTemplateModal"
      @replace-text="replaceText"
      @toggle-insert-article="toggleInsertArticle"
      @toggle-quoted-reply="toggleQuotedReply"
      @toggle-translation="toggleTranslation"
      @toggle-ai-panel="toggleAiPanel"
    />
    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
  <TeleportWithDirection to="body">
    <div
      v-if="showEmojiPicker"
      ref="emojiPickerWrapper"
      class="fixed z-[9999]"
      :style="emojiPickerStyle"
      @mousedown.stop
    >
      <EmojiInput
        class="!static !left-auto !right-auto !top-auto !bottom-auto before:!bottom-auto before:!top-[var(--emoji-picker-arrow-top)] before:!right-[-1rem] before:!left-auto before:!rotate-[270deg] rtl:before:!left-[-1rem] rtl:before:!right-auto rtl:before:!rotate-[90deg]"
        :on-click="addIntoEditor"
      />
    </div>
  </TeleportWithDirection>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-4;
}

.reply-box {
  transition: height 2s cubic-bezier(0.37, 0, 0.63, 1);

  @apply relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-4 -mt-px;

  textarea {
    @apply shadow-none outline-none border-transparent bg-transparent m-0 max-h-60 min-h-[2rem] pt-2 pb-0 px-0 resize-none;
  }
}

.normal-editor__canned-box {
  width: calc(100% - 2 * 1rem);
  left: 1rem;
}

// 移动端隐藏 PC 端的 AI 区域，PC 端隐藏移动端浮动面板
@media (max-width: 639px) {
  .reply-box__ai-generate,
  .reply-box__ai-suggestion {
    display: none !important;
  }

  .reply-box__top {
    textarea {
      min-height: 1.5rem !important;
      max-height: 8rem !important;
    }

    :deep(.ProseMirror-woot-style) {
      min-height: 1.5rem !important;
      max-height: 8rem !important;
    }

    :deep(.ProseMirror) {
      min-height: 1.5rem !important;
    }
  }
}

@media (min-width: 640px) {
  .reply-box__ai-panel {
    display: none !important;
  }
}
</style>
